import React, { useState } from 'react';
import useClient from 'api/client';
import TabsPanel from 'components/tabs/TabsPanel';
import { BackButton } from 'components/buttons';
import Header, { ButtonConfig } from 'components/header/Header';
import StatusWithTooltip from 'components/StatusWithTooltip/StatusWithTooltip';
import { useModal } from 'hooks/useModal';
import useDatasetCredentials from '../hooks/useDatasetCredentials';
import StackDetails, { StackType } from 'views/Stack/StackDetails';
import FolderList from './FolderList';
import SchemaViewer from './SchemaViewer';
import PermissionList from './Permissions';
import Uploader from './Uploader';
import TableList from './TableList';
import { NStatusTag } from '@innersource-nn/novoui-react';
import { trackEvent } from 'analytics/AWSRumService';
import EditDataset from './Modals/EditDatasetModal';
import useDatasetDetails from '../hooks/useDatasetDetails';
import EditorDetails from 'components/details/EditorDetails';
import SimpleLoader from 'components/loader/SimpleLoader';

interface SuccessDatasetProps {
    // dataset: Dataset;
    isUserAdmin: boolean;
    status: string;
}

const DatasetContent = ({ isUserAdmin, status }: SuccessDatasetProps) => {
    const client = useClient();
    const { items, dataset, error, loading, fetchDetails } =
        useDatasetDetails();

    const [editModalOpen, setEditModalOpen] = useState(false);

    // CUSTOM HOOKS:
    const {
        openDatasetAccess,
        openDeleteDataset,
        openCredentialsDataset,
        openAWSResourcesModal,
    } = useModal();

    const { credentials, credentialError, credentialLoading, copyCredentials } =
        useDatasetCredentials(isUserAdmin);

    const [counter, setCounter] = useState(1);

    const tooltipContent = !isUserAdmin
        ? 'To access this functionality you need to be part of the admin group the dataset belongs to.'
        : !dataset.enableCrawler
          ? 'Crawler needs to be enabled for this functionality. Click on the edit Button on the top right corner to enable the crawler'
          : '';

    const buttonConfigs: ButtonConfig[] = [
        {
            label: 'Request access',
            onClick: () =>
                openDatasetAccess({
                    resourceUri: dataset.datasetUri,
                    datasetName: dataset.label,
                }),
            icon: 'key',
        },
        {
            label: 'AWS Links',
            onClick: () =>
                openAWSResourcesModal({
                    datasetUri: dataset.datasetUri,
                    environmentUri: dataset.environment.environmentUri,
                    title: 'AWS Links',
                }),
            icon: 'external',
            tooltip: tooltipContent,
        },
        {
            label: 'Credentials',
            onClick: () =>
                openCredentialsDataset({
                    credentials,
                    loading: credentialLoading,
                    copyCredentials,
                }),
            icon: 'doselog',
            disabled: !isUserAdmin || credentialError !== '',
            tooltip: credentialError === '' ? tooltipContent : credentialError,
        },
        {
            label: 'Edit',
            onClick: () => setEditModalOpen(true),
            icon: 'edit',
            tooltip: tooltipContent,
        },
        {
            label: 'Remove',
            onClick: () =>
                openDeleteDataset({
                    label: dataset.label,
                    datasetUri: dataset.datasetUri,
                    terminationProtection: dataset.stack.terminationProtection,
                }),
            red: true,
            icon: 'trash',
            tooltip: tooltipContent,
        },
    ];

    const onChangeTab = (selectedTabIndex: number) => {
        if (selectedTabIndex === 1) {
            trackEvent('tab_click_dataset_schema_viewer', {
                location: 'dataset',
            });
        }
    };

    return loading ? (
        <SimpleLoader />
    ) : (
        <div>
            <EditDataset
                dataset={dataset}
                visible={editModalOpen}
                onClose={() => {
                    fetchDetails();
                    setEditModalOpen(false);
                }}
            />
            <BackButton counter={counter} />
            <Header
                title={dataset.label}
                buttonConfigs={
                    isUserAdmin
                        ? buttonConfigs
                        : buttonConfigs.filter(
                              (config) => config.label === 'Request access',
                          )
                }
            />
            <div className="flex gap-8 items-center">
                <div className="text-true-blue text-sm">
                    Created: {dataset.created}
                </div>
                <StatusWithTooltip content={status} />
                <NStatusTag
                    label={dataset.userRoleForDataset}
                    variant="rounded"
                    icon="person"
                />
            </div>
            <TabsPanel
                counter={counter}
                setCounter={setCounter}
                onChange={onChangeTab}
                tabs={[
                    {
                        label: 'overview',
                        component: (
                            <EditorDetails
                                expandable={true}
                                explandableTitle="Dataset Details"
                                error={error}
                                items={items}
                                type="dataset"
                            />
                        ),
                    },
                    {
                        label: 'schema ',
                        component: <SchemaViewer />,
                    },
                    {
                        label: 'tables',
                        component: (
                            <TableList
                                enableCrawler={dataset.enableCrawler ?? true}
                                datasetLabel={dataset.label}
                            />
                        ),
                    },
                    {
                        label: 'folders ',
                        component: <FolderList isUserAdmin={isUserAdmin} />,
                    },
                    {
                        label: 'permissions ',
                        component: <PermissionList />,
                    },
                    {
                        label: 'upload',
                        component: (
                            <Uploader dataset={dataset} client={client} />
                        ),
                        disabled: !isUserAdmin || !dataset.enableCrawler,
                        tooltip: tooltipContent,
                    },
                    {
                        label: 'stack ',
                        component: <StackDetails type={StackType.dataset} />,
                        disabled: !isUserAdmin,
                        tooltip: tooltipContent,
                    },
                ]}
            />
        </div>
    );
};

export default DatasetContent;
